import React, { useEffect, useState } from "react";

import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  styled,
} from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { Input, Select } from "Components/Form";
import AutoSuggest from "Components/Form/AutoSuggest";
import { useQuery } from "@tanstack/react-query";
import { UserKeys } from "Util/queries";
import UserService from "Services/user.service";
import { useAppState } from "Context/StateContext";
import { ContactCronjob, Cronjob } from "Models/cronjob";
import { CronjobFrequency, Status, WeekDays } from "Util/enums";
import { TFunction, useTranslation } from "react-i18next";
import { User } from "Models/user";
import * as yup from "yup";
import useYupValidation from "Hooks/useYupValidation";
import { SubscribersDesc } from "./styles";

export const RadioCard = styled("div")(({ isSelected }: { isSelected?: boolean }) => ({
  flex: 1,
  backgroundColor: isSelected ? colors.white : "transparent",
  padding: "14px 16px",
  borderRadius: "8px",
  border: `1px solid ${colors.gray200}`,
}));

export const RadioTitle = styled("div")(() => ({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "20px",
  letterSpacing: "0.25px",
}));

export const RadioSubtitle = styled("div")(() => ({
  color: colors.gray600,
  textOverflow: "ellipsis",
  fontFamily: "Manrope",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.4px",
}));

interface EmailSubscriptionFormSectionProps {
  cronJob: Cronjob;
  updateCronJob: (cronJob?: Cronjob) => void;
  setChanges: (change: string) => void;
  errors?: yup.ValidationError[];
}

const getSendEveryLabel = (
  frequency: CronjobFrequency,
  translate: TFunction<"translation", undefined>
) => {
  switch (frequency) {
    case CronjobFrequency.ONCE:
      return translate("sendOn");
    case CronjobFrequency.WEEKLY:
      return translate("sendEvery");
    case CronjobFrequency.MONTHLY:
      return translate("sendOn");
    default:
      return translate("sendEvery");
  }
};

export const EmailSubscriptionFormSection = (props: EmailSubscriptionFormSectionProps) => {
  const { cronJob, updateCronJob, setChanges, errors = [] } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const daysOfTheWeek = [
    translate(WeekDays.SUNDAY),
    translate(WeekDays.MONDAY),
    translate(WeekDays.TUESDAY),
    translate(WeekDays.WEDNESDAY),
    translate(WeekDays.THURSDAY),
    translate(WeekDays.FRIDAY),
    translate(WeekDays.SATURDAY),
  ];

  const daysOfTheMonth = Array.from(Array(28).keys()).map((day) => day + 1);

  const emailFrequencyOptions = [
    {
      title: translate("oneTime"),
      value: CronjobFrequency.ONCE,
      description: translate("receiveOnlyOnce"),
    },
    {
      title: translate("weekly"),
      value: CronjobFrequency.WEEKLY,
      description: translate("receiveEveryWeek"),
    },
    {
      title: translate("monthly"),
      value: CronjobFrequency.MONTHLY,
      description: translate("receiveOnceAMonth"),
    },
  ];

  const { data: userList } = useQuery({
    queryKey: UserKeys.getAll([]),
    queryFn: async () => {
      return UserService.getAll({
        attributes: JSON.stringify([
          "id",
          "name",
          "lastName",
          "fullName",
          "typeUser",
          "code",
          "email",
          "phone",
          "companyId",
          "status",
        ]),
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            where: { status: Status.ACTIVE, companyId: currentUser?.companyId, typeUser: "Admin" },
          },
        ]),
        sortBy: JSON.stringify([["name", "asc"]]),
      }).then(({ data }) => data);
    },
    initialData() {
      return [];
    },
    enabled: !!currentUser?.companyId,
  });

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <div>
        <AutoSuggest
          label={translate("addAdminUser")}
          placeholder={translate("searchByNameOrEmail")}
          freeSolo
          multiple
          required
          getOptionLabel={(option: User) => `${option.fullName} - ${option.email}`}
          getOptionSelected={(option: User, value: User) => option.id === value.id}
          value={cronJob?.cronjobContacts?.map((value) => value.user) || []}
          onChange={(users: User[]) => {
            if (!users?.length)
              updateCronJob({ ...(cronJob || {}), cronjobContacts: [], contactEmails: [] });
            else {
              updateCronJob({
                ...(cronJob || {}),
                cronjobContacts: users.map((user) => ({
                  mail: user.email,
                  user,
                  userId: user.id,
                })) as ContactCronjob[],
                contactEmails: users.map((user) => user.email),
              });
            }
            setChanges("Subscribers");
          }}
          options={userList || []}
          error={fieldHasError("cronjobContacts")}
          errorMessage={getErrorByField("cronjobContacts")}
        />
        {fieldHasError("cronjobContacts") ? null : (
          <SubscribersDesc>{translate("subscriberMustBeRegisteredOnShipeezi")}</SubscribersDesc>
        )}
      </div>
      <div>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">{translate("emailFrequency")}</FormLabel>
          <RadioGroup
            aria-label="gender"
            name="gender1"
            value={cronJob?.frequency}
            onChange={(e) => {
              updateCronJob({
                ...(cronJob || {}),
                frequency: e.currentTarget.value as CronjobFrequency,
              });
              setChanges("Frequency");
            }}
          >
            <Box display="flex" gap="4px" alignItems="center">
              {emailFrequencyOptions?.map((option) => {
                return (
                  <RadioCard key={option.value} isSelected={cronJob?.frequency === option?.value}>
                    <FormControlLabel
                      value={option?.value}
                      control={<Radio color="primary" />}
                      label={
                        <Box display="flex" flexDirection="column">
                          <RadioTitle>{option?.title}</RadioTitle>
                          <RadioSubtitle>{option?.description}</RadioSubtitle>
                        </Box>
                      }
                    />
                  </RadioCard>
                );
              })}
            </Box>
          </RadioGroup>
        </FormControl>
      </div>
      {cronJob?.frequency !== CronjobFrequency.ONCE && (
        <Box display="flex" gap="16px">
          {cronJob?.frequency === CronjobFrequency.WEEKLY ? (
            <Box flex="1">
              <Select
                required
                label={cronJob?.frequency && getSendEveryLabel(cronJob?.frequency, translate)}
                value={cronJob?.day}
                onChange={(e: SelectChangeEvent<any>) =>
                  updateCronJob({ ...(cronJob || {}), day: e.target.value })
                }
                error={fieldHasError("day")}
                errorMessage={getErrorByField("day")}
              >
                {daysOfTheWeek.map((day, index) => (
                  <MenuItem key="`${day}-${index}`" value={index}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ) : (
            <Box flex="1">
              <Select
                required
                label={cronJob?.frequency && getSendEveryLabel(cronJob?.frequency, translate)}
                value={cronJob?.day}
                onChange={(e: SelectChangeEvent<any>) =>
                  updateCronJob({ ...(cronJob || {}), day: e.target.value })
                }
                error={fieldHasError("day")}
                errorMessage={getErrorByField("day")}
              >
                {daysOfTheMonth.map((day) => (
                  <MenuItem key="`${day}-${index}`" value={day}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
              {fieldHasError("day") ? null : (
                <SubscribersDesc>{translate("enterADayOfTheMonthBetween1and28")}</SubscribersDesc>
              )}
            </Box>
          )}

          <Box flex="1">
            <Input
              required
              label="Time"
              type="time"
              value={cronJob.time}
              onChange={(e) => updateCronJob({ ...(cronJob || {}), time: e.target.value })}
              error={fieldHasError("time")}
              errorMessage={getErrorByField("time")}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
